import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  User, 
  Settings, 
  LogOut, 
  AlertCircle, 
  FileText, 
  GitBranch, 
  Package, 
  Shield, 
  CalendarClock, 
  AlertTriangle, 
  GraduationCap, 
  Users, 
  Building2,
  Building,
  Gauge,
  ChevronDown,
  BookOpen,
  Search,
  Eye,
  EyeOff,
  Wrench,
  BarChart3,
  FileQuestion,
  MapPin,
  ClipboardList
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { AuthModal } from './auth/AuthModal';
import { TourSelector } from './TourSelector';
import { UserManagementPanel } from './UserManagementPanel';
import { ClientManagementPanel } from './ClientManagementPanel';

export function Header({ 
  isSharedView = false, 
  onOpenActiveIssues, 
  onOpenReference, 
  onOpenWorkflows,
  onOpenAssets,
  onOpenInstrumentation,
  onOpenBuildings,
  onOpenCompliance,
  onOpenPlannedChanges,
  onOpenRiskExposure,
  onOpenCompetency,
  onOpenGlobalSearch,
  onOpenTrainingModules,
  onOpenMaintenanceProcedures,
  onOpenMaintenanceDashboard,
  onOpenMaintenanceEquipment,
  onOpenQuizzes,
  onOpenELearningAdmin,
  onOpenHotspotManager,
  onOpenHotspotPlacement,
  onOpenInfoHotspots,
  isPreviewMode = false,
  onTogglePreviewMode
}) {
  const { user, isAuthenticated, isAdmin, isViewer, isMaintenanceTech, hasFullFeatures, logout, loading } = useAuth();
  const { currentTour } = useTour();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [isClientManagementOpen, setIsClientManagementOpen] = useState(false);

  // Industries that get full management panels (Planned Changes, Risk, Compliance, etc.)
  const fullFeatureIndustries = ['oil_gas', 'facility', 'commercial'];
  const hasFullIndustryFeatures = fullFeatureIndustries.includes(currentTour?.industry?.toLowerCase());
  
  // Simplified industries (surplus, industrial, marine) - only get basic navigation, no management panels
  const simplifiedIndustries = ['surplus', 'industrial', 'marine'];
  const isSimplifiedTour = simplifiedIndustries.includes(currentTour?.industry?.toLowerCase());
  
  // Training-type industries - shows Assets, Training Modules, Quizzes, Hotspots, and Maintenance Procedures
  const trainingIndustries = ['training', 'sato_canada'];
  const isTrainingTour = trainingIndustries.includes(currentTour?.industry?.toLowerCase());
  
  // Maintenance techs always get simplified view (like training)
  const isMaintenanceTechView = isMaintenanceTech;
  
  // In preview mode, hide admin features
  const effectiveIsAdmin = isAdmin && !isPreviewMode;
  
  // Show full management panels for:
  // 1. Admins (non-preview mode) on any non-simplified tour
  // 2. ANY authenticated user (including viewers) on full-feature industry tours
  // BUT NOT for training tours or maintenance tech view (they have their own specific buttons)
  const showManagementPanels = !isSimplifiedTour && !isTrainingTour && !isMaintenanceTechView && (effectiveIsAdmin || hasFullIndustryFeatures);
  
  // Show search for full-feature industries (all users including viewers) but not for maintenance techs
  const showSearch = !isSimplifiedTour && !isTrainingTour && !isMaintenanceTechView && (effectiveIsAdmin || hasFullIndustryFeatures);
  
  // Show training-specific panels for training-type industry tours OR maintenance tech users
  const showTrainingPanels = isTrainingTour || isMaintenanceTechView;

  return (
    <>
      <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Zentara Logo - Z Icon Image */}
          <div className="flex items-center justify-center w-10 h-10 rounded-lg overflow-hidden">
            <img 
              src="https://customer-assets.emergentagent.com/job_7a888182-d9ad-4911-af25-412e3d52e004/artifacts/oj6wptvw_Zentara%20As-Ltd%20-%20Just%20Z.png" 
              alt="Zentara" 
              className="w-10 h-10 object-contain"
            />
          </div>
          <div>
            {isSharedView ? (
              <>
                <p className="text-sm font-medium text-foreground">
                  {currentTour?.name || 'Shared Scan'}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Powered by Zentara
                </p>
              </>
            ) : (
              <p className="text-[10px] text-muted-foreground">
                Industrial & Commercial Asset Management
              </p>
            )}
          </div>
          <Badge variant="outline" className="ml-2 text-[10px] border-primary/30 text-primary">
            {currentTour?.industry === 'sato_canada' ? 'Sato Canada' : 
             currentTour?.industry === 'oil_gas' ? 'Oil & Gas' :
             currentTour?.industry?.charAt(0).toUpperCase() + currentTour?.industry?.slice(1) || 'Industrial'}
          </Badge>

          {/* Tour Selector - Only show for authenticated users */}
          {isAuthenticated && !isSharedView && (
            <div className="ml-4 hidden md:block">
              <TourSelector />
            </div>
          )}
        </div>

        {/* Scrollable buttons container */}
        <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent pb-1 max-w-[calc(100vw-280px)] md:max-w-none">
          {/* TRAINING INDUSTRY SPECIFIC BUTTONS */}
          {/* Assets Button - For Training tours */}
          {isAuthenticated && !isSharedView && showTrainingPanels && onOpenAssets && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenAssets}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Assets</span>
            </Button>
          )}

          {/* Training & Maintenance Group - For Training tours */}
          {isAuthenticated && !isSharedView && showTrainingPanels && (onOpenTrainingModules || onOpenMaintenanceProcedures || onOpenMaintenanceDashboard) && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-r from-blue-500/10 via-slate-500/10 to-emerald-500/10 border border-blue-500/20">
              <span className="text-xs text-muted-foreground mr-1 hidden lg:inline">Training:</span>
              
              {/* Training Modules Button */}
              {onOpenTrainingModules && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenTrainingModules}
                  className="gap-1.5 text-blue-500 hover:bg-blue-500/20 h-7 px-2"
                >
                  <GraduationCap className="h-4 w-4" />
                  <span className="hidden sm:inline">Modules</span>
                </Button>
              )}

              {/* Maintenance Procedures Button */}
              {onOpenMaintenanceProcedures && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenMaintenanceProcedures}
                  className="gap-1.5 text-slate-500 hover:bg-slate-500/20 h-7 px-2"
                >
                  <Wrench className="h-4 w-4" />
                  <span className="hidden sm:inline">Procedures</span>
                </Button>
              )}

              {/* Dashboard Button */}
              {onOpenMaintenanceDashboard && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenMaintenanceDashboard}
                  className="gap-1.5 text-emerald-500 hover:bg-emerald-500/20 h-7 px-2"
                >
                  <BarChart3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Button>
              )}
            </div>
          )}
          
          {/* Maintenance Equipment Button - Available for ALL industry types */}
          {isAuthenticated && !isSharedView && onOpenMaintenanceEquipment && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenMaintenanceEquipment}
              className="gap-2 border-cyan-500/30 text-cyan-500 hover:bg-cyan-500/10 shrink-0"
            >
              <Wrench className="h-4 w-4" />
              <span className="hidden sm:inline">Equipment</span>
            </Button>
          )}
          
          {/* Info Hotspots Group - Available for ALL industry types */}
          {isAuthenticated && !isSharedView && (onOpenHotspotPlacement || onOpenInfoHotspots) && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-r from-emerald-500/10 to-amber-500/10 border border-emerald-500/20">
              <span className="text-xs text-muted-foreground mr-1 hidden lg:inline">Hotspots:</span>
              
              {/* Place Hotspot Button - Admin Only */}
              {effectiveIsAdmin && onOpenHotspotPlacement && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenHotspotPlacement}
                  className="gap-1.5 text-emerald-500 hover:bg-emerald-500/20 h-7 px-2"
                  data-testid="place-hotspot-header-btn"
                >
                  <MapPin className="h-4 w-4" />
                  <span className="hidden sm:inline">Place</span>
                </Button>
              )}
              
              {/* View Hotspots Button */}
              {onOpenInfoHotspots && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenInfoHotspots}
                  className="gap-1.5 text-amber-500 hover:bg-amber-500/20 h-7 px-2"
                  data-testid="view-hotspots-header-btn"
                >
                  <BookOpen className="h-4 w-4" />
                  <span className="hidden sm:inline">View</span>
                </Button>
              )}
            </div>
          )}

          {/* E-Learning Group - Training Industry Only */}
          {isAuthenticated && !isSharedView && showTrainingPanels && (onOpenQuizzes || onOpenELearningAdmin) && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-r from-purple-500/10 to-amber-500/10 border border-purple-500/20">
              <span className="text-xs text-muted-foreground mr-1 hidden lg:inline">E-Learning:</span>
              
              {/* Quizzes Button */}
              {onOpenQuizzes && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenQuizzes}
                  className="gap-1.5 text-purple-500 hover:bg-purple-500/20 h-7 px-2"
                >
                  <FileQuestion className="h-4 w-4" />
                  <span className="hidden sm:inline">Quizzes</span>
                </Button>
              )}

              {/* Results Button - Admin Only */}
              {effectiveIsAdmin && onOpenELearningAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenELearningAdmin}
                  className="gap-1.5 text-amber-500 hover:bg-amber-500/20 h-7 px-2"
                >
                  <ClipboardList className="h-4 w-4" />
                  <span className="hidden sm:inline">Results</span>
                </Button>
              )}
            </div>
          )}

          {/* FULL-FEATURE INDUSTRY BUTTONS */}
          {/* Global Search Button - Available to admins always, or for full-feature industries */}
          {isAuthenticated && !isSharedView && showSearch && onOpenGlobalSearch && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenGlobalSearch}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Search</span>
            </Button>
          )}

          {/* 1. Buildings Button - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && onOpenBuildings && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenBuildings}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <Building className="h-4 w-4" />
              <span className="hidden sm:inline">Buildings</span>
            </Button>
          )}

          {/* 2. Assets Dropdown with Instrumentation submenu - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && (onOpenAssets || onOpenInstrumentation) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
                >
                  <Package className="h-4 w-4" />
                  <span className="hidden sm:inline">Assets</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {onOpenAssets && (
                  <DropdownMenuItem onClick={onOpenAssets} className="gap-2">
                    <Package className="h-4 w-4" />
                    Assets
                  </DropdownMenuItem>
                )}
                {onOpenInstrumentation && (
                  <DropdownMenuItem onClick={onOpenInstrumentation} className="gap-2">
                    <Gauge className="h-4 w-4" />
                    Instrumentation
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* 3. Active Issues Button - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && onOpenActiveIssues && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenActiveIssues}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Active Issues</span>
            </Button>
          )}

          {/* 4. Risk & Exposure Button - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && onOpenRiskExposure && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenRiskExposure}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">Risk & Exposure</span>
            </Button>
          )}

          {/* 5. Planned Changes Button - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && onOpenPlannedChanges && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenPlannedChanges}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <CalendarClock className="h-4 w-4" />
              <span className="hidden sm:inline">Planned Changes</span>
            </Button>
          )}

          {/* 6. Workflows Button - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && onOpenWorkflows && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenWorkflows}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <GitBranch className="h-4 w-4" />
              <span className="hidden sm:inline">Workflows</span>
            </Button>
          )}

          {/* 7. Competency Button - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && onOpenCompetency && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenCompetency}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <GraduationCap className="h-4 w-4" />
              <span className="hidden sm:inline">Competency</span>
            </Button>
          )}

          {/* 8. Compliance Button - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && onOpenCompliance && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenCompliance}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Compliance</span>
            </Button>
          )}

          {/* 9. Reference Button - Available for full-feature industries */}
          {isAuthenticated && !isSharedView && showManagementPanels && onOpenReference && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenReference}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Reference</span>
            </Button>
          )}

          {/* Preview Mode Toggle - Admin Only */}
          {isAuthenticated && isAdmin && !isSharedView && (
            <Button
              variant={isPreviewMode ? "default" : "outline"}
              size="sm"
              onClick={onTogglePreviewMode}
              className={isPreviewMode 
                ? "gap-2 bg-amber-500 hover:bg-amber-600 text-white" 
                : "gap-2 border-primary/30 text-primary hover:bg-primary/10"
              }
              title={isPreviewMode ? "Exit Preview Mode" : "Preview as Client"}
            >
              {isPreviewMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span className="hidden sm:inline">{isPreviewMode ? "Exit Preview" : "Preview"}</span>
            </Button>
          )}

          {/* User Management Button - Admin Only (hidden in Preview mode) */}
          {isAuthenticated && isAdmin && !isSharedView && !isPreviewMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsUserManagementOpen(true)}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Users</span>
            </Button>
          )}

          {/* Client Management Button - Admin Only (hidden in Preview mode) */}
          {isAuthenticated && isAdmin && !isSharedView && !isPreviewMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsClientManagementOpen(true)}
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            >
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Clients</span>
            </Button>
          )}
          </div>

          {/* User Menu - Always visible, outside scroll container */}
          <div className="shrink-0 ml-2">
          {loading ? (
            <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
          ) : isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="text-sm hidden md:inline">{user.name || 'User'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => setIsUserManagementOpen(true)}>
                    <Users className="mr-2 h-4 w-4" />
                    User Management
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-destructive cursor-pointer"
                  onClick={logout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : !isSharedView ? (
            <Button 
              variant="outline" 
              size="sm" 
              className="border-primary/30 text-primary hover:bg-primary/10"
              onClick={() => setIsAuthModalOpen(true)}
            >
              Sign In
            </Button>
          ) : isSharedView ? (
            <Button 
              variant="outline" 
              size="sm" 
              className="border-primary/30 text-primary hover:bg-primary/10"
              onClick={() => window.location.href = '/'}
            >
              Go to Portal
            </Button>
          ) : null}
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />

      {/* User Management Panel */}
      <UserManagementPanel
        isOpen={isUserManagementOpen}
        onClose={() => setIsUserManagementOpen(false)}
      />

      {/* Client Management Panel */}
      <ClientManagementPanel
        isOpen={isClientManagementOpen}
        onClose={() => setIsClientManagementOpen(false)}
      />
    </>
  );
}

export default Header;
